import { useCallback, useEffect, useState } from "react";
import {
  FiRefreshCw,
  FiShoppingCart,
  FiTruck,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const number = (value) => Number(value || 0);

const typeConfig = (type, quantityChange) => {
  const isIncoming = number(quantityChange) > 0;

  if ((type || "").includes("TRANSFER")) {
    return {
      label: "Transfer",
      icon: FiRefreshCw,
      color: "blue",
    };
  }

  if (
    ["OPENING_BALANCE", "ADJUSTMENT_IN", "STOCK_COUNT_GAIN"].includes(type)
  ) {
    return {
      label: type === "OPENING_BALANCE" ? "Opening Stock" : "Stock In",
      icon: FiTruck,
      color: "green",
    };
  }

  if (
    ["ADJUSTMENT_OUT", "STOCK_COUNT_LOSS"].includes(type)
  ) {
    return {
      label: "Stock Out",
      icon: FiShoppingCart,
      color: "red",
    };
  }

  return {
    label: isIncoming ? "Stock In" : "Stock Out",
    icon: isIncoming ? FiTruck : FiShoppingCart,
    color: isIncoming ? "green" : "red",
  };
};

const time = (dateValue) =>
  dateValue
    ? new Date(dateValue).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

export default function RecentStockActivity() {
  const { accessToken } = useAuth();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadActivities = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      const movements = await apiRequest("/inventory/movements", {
        accessToken,
      });

      setActivities((movements || []).slice(0, 6));
    } catch {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <h2 className="mb-6 text-lg font-bold text-gray-900">
        Recent Stock Activity
      </h2>

      <div className="space-y-5">
        {loading ? (
          <p className="py-6 text-center text-sm text-gray-500">
            Loading stock activity…
          </p>
        ) : activities.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No stock activity recorded yet.
          </p>
        ) : (
          activities.map((movement) => {
            const config = typeConfig(
              movement.type,
              movement.quantityChange,
            );

            const Icon = config.icon;
            const isIncoming = number(movement.quantityChange) > 0;

            return (
              <div
                key={movement.id}
                className="flex items-start gap-4 border-b pb-5 last:border-0"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                  <Icon size={20} />
                </div>

                <div className="flex-1">
                  <div className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="truncate font-semibold text-gray-900">
                        {movement.variant?.product?.name ||
                          movement.variant?.sku ||
                          "Product Variant"}
                      </h4>

                      <p className="mt-1 text-sm text-gray-500">
                        {movement.referenceType
                          ? `${movement.referenceType} · `
                          : ""}
                        {movement.referenceId
                          ? movement.referenceId.slice(0, 8)
                          : movement.store?.name || "Inventory movement"}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <p
                        className={`font-bold ${
                          isIncoming ? "text-green-600" : "text-red-500"
                        }`}
                      >
                        {isIncoming ? "+" : ""}
                        {number(movement.quantityChange).toLocaleString(
                          "en-IN",
                        )}
                      </p>

                      <p className="text-xs text-gray-400">
                        {time(movement.occurredAt)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-2">
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${
                        config.color === "green"
                          ? "bg-green-50 text-green-700"
                          : config.color === "red"
                            ? "bg-red-50 text-red-600"
                            : "bg-blue-50 text-blue-700"
                      }`}
                    >
                      {config.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}